import React from 'react';

import type { ConfirmDialogProps } from '@lumapps/lumx-dialogs/components/ConfirmDialog';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { wrapStringGroupWithComponent } from '@lumapps/utils/string/wrapStringGroupWithComponent';

import { updateStatus } from '../api/contents';
import { CONTENTS } from '../keys';
import { LegacyContentStatus, type Content } from '../types';

export const useContentUnpublish = ({
    openConfirmDialog,
    contentId,
    contentTitle = '',
    onSuccess,
}: {
    contentId?: string;
    contentTitle: string;
    openConfirmDialog: (
        props: Pick<ConfirmDialogProps, 'title' | 'confirmMessage' | 'onConfirm' | 'confirmProps' | 'id'>,
    ) => void;
    onSuccess: (content: Content) => void;
}) => {
    const { success, error } = useNotification();
    const { translateKey, translateAndReplace } = useTranslate();

    const unpublishContent = React.useCallback(async () => {
        if (!contentId) {
            return;
        }

        try {
            const { data: updatedContent } = await updateStatus({ contentId, status: LegacyContentStatus.draft });

            success({ content: translateKey(CONTENTS.UNPUBLISH_SUCCESS) });
            onSuccess(updatedContent);
        } catch (err) {
            error({ content: translateKey(GLOBAL.GENERIC_ERROR) });
        }
    }, [contentId, error, onSuccess, success, translateKey]);

    const onUnpublish = React.useCallback(() => {
        openConfirmDialog({
            title: translateKey(CONTENTS.CONFIRM_UNPUBLISH),
            confirmMessage: wrapStringGroupWithComponent(
                translateAndReplace(CONTENTS.EXPLAIN_UNPUBLISH_ONE, {
                    CONTENT_NAME: contentTitle || '',
                }),
                {
                    start: '<strong>',
                    end: '</strong>',
                },
                (value) => <strong>{value}</strong>,
            ),
            onConfirm: unpublishContent,
            confirmProps: { label: translateKey(CONTENTS.UNPUBLISH) },
            id: 'unpublish',
        });
    }, [contentTitle, openConfirmDialog, translateAndReplace, translateKey, unpublishContent]);

    return {
        onUnpublish,
    };
};
